import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { ApiParkingMasterDataService } from '../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MapperParkingMasterDataService } from '../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ParkingVehicleType, ParkingVehicleTypeDto } from '../../../../core/models/system-settings/parking-master-data.model';
import {
  AllocateClientFreeQuotaPayload,
  CLIENT_FREE_QUOTA_MANAGED_BY_OPTIONS,
  ClientFreeQuota,
  ClientFreeQuotaSummary,
  FreeParkingSubscriptionOption,
  FreeQuotaActivityEntityTypes,
  FreeQuotaClientOption,
  SITE_FREE_QUOTA_STATUS_OPTIONS,
  SiteFreeQuota,
  SaveSiteFreeQuotaPayload,
  UpdateClientFreeQuotaPayload,
  clientFreeQuotaManagedByLabel,
  createEmptySiteFreeQuotaForm,
  freeQuotaActivityEntityId,
  mapClientFreeQuotaList,
  mapClientFreeQuotaSummary,
  mapFreeParkingSubscriptionList,
  mapFreeQuotaClientOptionList,
  mapSiteFreeQuotaList,
} from '../../../../core/models/system-settings/free-quota.model';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { PssConfirmModalComponent, PssConfirmModalVariant } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';

@Component({
  selector: 'app-site-free-quota',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent, PssConfirmModalComponent],
  templateUrl: './site-free-quota.component.html',
  styleUrl: './site-free-quota.component.css',
})
export class SiteFreeQuotaComponent implements OnInit, OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiSystemSettingsService);
  private readonly apiMaster = inject(ApiParkingMasterDataService);
  private readonly masterMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly statusOptions = SITE_FREE_QUOTA_STATUS_OPTIONS;
  readonly managedByOptions = CLIENT_FREE_QUOTA_MANAGED_BY_OPTIONS;
  readonly activityEntityTypes = FreeQuotaActivityEntityTypes;
  readonly pageSizeOptions = [20, 50, 100];
  readonly managedByLabel = clientFreeQuotaManagedByLabel;

  /* ---------- Site quota (few rows — one per vehicle type) ---------- */
  siteQuotas = signal<SiteFreeQuota[]>([]);
  vehicleTypes = signal<ParkingVehicleType[]>([]);
  freeSubscriptions = signal<FreeParkingSubscriptionOption[]>([]);
  isSiteLoading = signal(false);
  showGuide = signal(false);

  /* ---------- Client quota table (server paging) ---------- */
  clientRows = signal<ClientFreeQuota[]>([]);
  clientSummary = signal<ClientFreeQuotaSummary>(mapClientFreeQuotaSummary(null));
  clientTotal = signal(0);
  clientTotalPages = signal(0);
  clientPage = signal(1);
  clientPageSize = signal(20);
  isClientLoading = signal(false);
  clientSearch = '';
  filterVehicleTypeId = 0;
  filterManagedBy = '';

  /* ---------- Site quota modal ---------- */
  isSiteModalOpen = signal(false);
  isSaving = signal(false);
  quotaForm: SaveSiteFreeQuotaPayload = createEmptySiteFreeQuotaForm(0);

  /* ---------- Allocate modal ---------- */
  isAllocateModalOpen = signal(false);
  isAllocating = signal(false);
  /** opened from a table row — client fixed */
  allocateLocked = false;
  allocateClient = signal<FreeQuotaClientOption | null>(null);
  allocateVehicleTypeId = 0;
  allocateAdditionalQuota = 1;
  allocateManagedBy = 'site_building';
  clientPickerSearch = '';
  clientOptions = signal<FreeQuotaClientOption[]>([]);
  isClientOptionsLoading = signal(false);

  /* ---------- Edit client modal ---------- */
  isEditModalOpen = signal(false);
  isUpdatingClient = signal(false);
  editRow = signal<ClientFreeQuota | null>(null);
  editClientQuotaApproved = 0;
  editClientManagedBy = 'site_building';

  /* ---------- History ---------- */
  showActivityHistory = false;
  historyEntityType: string = FreeQuotaActivityEntityTypes.SiteFreeQuota;
  historyEntityId = 0;
  historyTitle = '';
  historySubtitle = '';

  /* ---------- Confirm ---------- */
  showConfirm = false;
  confirmTitle = 'ยืนยันการดำเนินการ?';
  confirmMessage = '';
  confirmHighlight = '';
  confirmMessageSuffix = 'ดำเนินการต่อหรือไม่?';
  confirmVariant: PssConfirmModalVariant = 'warning';
  confirmIcon = 'help';
  confirmLoading = false;
  private pendingConfirmAction: (() => void) | null = null;

  private clientRequestSeq = 0;
  private optionsRequestSeq = 0;
  private clientPickerTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      untracked(() => {
        this.closeAllModals();
        this.resetClientFilters();
        if (!site?.id) {
          this.siteQuotas.set([]);
          this.clientRows.set([]);
          this.clientSummary.set(mapClientFreeQuotaSummary(null));
          this.clientTotal.set(0);
          this.clientTotalPages.set(0);
          this.freeSubscriptions.set([]);
          return;
        }
        this.loadAll();
      });
    });
  }

  ngOnInit(): void {
    this.loadVehicleTypes();
  }

  ngOnDestroy(): void {
    if (this.clientPickerTimer) clearTimeout(this.clientPickerTimer);
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get hasClientFilter(): boolean {
    return !!this.clientSearch.trim() || this.filterVehicleTypeId > 0 || !!this.filterManagedBy;
  }

  get startItemIndex(): number {
    return this.clientTotal() === 0 ? 0 : (this.clientPage() - 1) * this.clientPageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.clientPage() * this.clientPageSize(), this.clientTotal());
  }

  get pagesArray(): number[] {
    const total = this.clientTotalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.clientPage() - 2);
    const end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return end >= start ? Array.from({ length: end - start + 1 }, (_, i) => start + i) : [];
  }

  /** vehicle types not yet configured (adding) + the current one (editing) */
  get siteModalVehicleTypes(): ParkingVehicleType[] {
    const used = new Set(this.siteQuotas().map((q) => q.vehicleTypeId));
    return this.vehicleTypes().filter((vt) => !used.has(vt.id) || vt.id === this.quotaForm.vehicleTypeId);
  }

  get filteredFreeSubscriptions(): FreeParkingSubscriptionOption[] {
    const vt = this.quotaForm.vehicleTypeId;
    const all = this.freeSubscriptions();
    if (!vt) return all;
    return all.filter((s) => !s.vehicleTypeId || s.vehicleTypeId === vt);
  }

  /** allocate only from vehicle types that have an active site pool */
  get allocatableSiteQuotas(): SiteFreeQuota[] {
    return this.siteQuotas().filter((q) => q.status === 'active');
  }

  get allocatePool(): SiteFreeQuota | null {
    return this.siteQuotas().find((q) => q.vehicleTypeId === this.allocateVehicleTypeId) ?? null;
  }

  get editDelta(): number {
    const row = this.editRow();
    return row ? this.editClientQuotaApproved - row.quotaApproved : 0;
  }

  get editPool(): SiteFreeQuota | null {
    const row = this.editRow();
    return row ? this.siteQuotas().find((q) => q.vehicleTypeId === row.vehicleTypeId) ?? null : null;
  }

  statusLabel(status: string): string {
    return this.statusOptions.find((o) => o.value === status)?.label ?? status;
  }

  usagePercent(used: number, approved: number): number {
    if (approved <= 0) return used > 0 ? 100 : 0;
    return Math.min(100, Math.round((used / approved) * 100));
  }

  trackQuota(_: number, row: SiteFreeQuota): number {
    return row.id || row.vehicleTypeId;
  }

  trackClientRow(_: number, row: ClientFreeQuota): number {
    return row.id;
  }

  trackOption(_: number, row: FreeQuotaClientOption): number {
    return row.id;
  }

  onRefresh(): void {
    this.loadAll();
  }

  /* ---------- Client table: search / filter / paging ---------- */

  onClientSearch(): void {
    this.clientPage.set(1);
    this.loadClientTable();
  }

  clearClientFilters(): void {
    this.resetClientFilters();
    this.loadClientTable();
  }

  setClientPage(page: number): void {
    if (page < 1 || page > this.clientTotalPages() || page === this.clientPage()) return;
    this.clientPage.set(page);
    this.loadClientTable();
  }

  onClientPageSizeChange(size: number): void {
    this.clientPageSize.set(Number(size));
    this.clientPage.set(1);
    this.loadClientTable();
  }

  /* ---------- Site quota modal ---------- */

  openSiteQuotaModal(row?: SiteFreeQuota): void {
    if (!this.selectedSiteId) return;
    if (!row && !this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    this.quotaForm = row
      ? {
          userId: 0,
          menuId: 0,
          id: row.id,
          siteId: row.siteId,
          vehicleTypeId: row.vehicleTypeId,
          subscriptionId: row.subscriptionId,
          quotaApproved: row.quotaApproved,
          status: row.status,
        }
      : createEmptySiteFreeQuotaForm(this.selectedSiteId);
    this.isSiteModalOpen.set(true);
  }

  closeSiteQuotaModal(): void {
    if (this.isSaving()) return;
    this.isSiteModalOpen.set(false);
  }

  onVehicleTypeChange(): void {
    const allowed = this.filteredFreeSubscriptions.map((s) => s.id);
    if (this.quotaForm.subscriptionId && !allowed.includes(this.quotaForm.subscriptionId)) {
      this.quotaForm.subscriptionId = null;
    }
  }

  onSaveQuota(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.toast.error('กรุณาเลือกลานจากแถบด้านข้าง');
      return;
    }
    if (!this.quotaForm.vehicleTypeId) {
      this.toast.error('กรุณาเลือกประเภทรถ');
      return;
    }
    if (this.quotaForm.quotaApproved < 0) {
      this.toast.error('โควต้าที่อนุมัติต้องไม่ติดลบ');
      return;
    }
    const existing = this.siteQuotas().find((q) => q.id === this.quotaForm.id);
    if (existing && this.quotaForm.quotaApproved < existing.quotaUsed + existing.quotaAllocatedToClients) {
      this.toast.error(
        `ลดได้ไม่ต่ำกว่า ใช้แล้ว + แจก Client (${existing.quotaUsed + existing.quotaAllocatedToClients})`,
      );
      return;
    }

    const auth = this.resolveAuth();
    if (!auth) return;

    const payload: SaveSiteFreeQuotaPayload = {
      ...this.quotaForm,
      ...auth,
      siteId,
      id: this.quotaForm.id && this.quotaForm.id > 0 ? this.quotaForm.id : null,
      subscriptionId: this.quotaForm.subscriptionId && this.quotaForm.subscriptionId > 0
        ? this.quotaForm.subscriptionId
        : null,
    };

    this.askConfirm({
      title: payload.id ? 'ยืนยันแก้ไขโควต้าลาน?' : 'ยืนยันตั้งโควต้าลาน?',
      message: 'จะบันทึกจำนวนอนุมัติโควต้าจอดฟรีของลาน',
      highlight: `${this.vehicleTypeName(payload.vehicleTypeId)} · อนุมัติ ${payload.quotaApproved}`,
      variant: 'warning',
      icon: 'save',
    }, () => {
      this.isSaving.set(true);
      this.confirmLoading = true;
      this.api.saveSiteFreeQuota(payload).subscribe({
        next: (res) => {
          console.log('saveSiteFreeQuota', res);
          if (res?.isSuccess ?? res?.IsSuccess) {
            this.toast.success(res?.message ?? res?.Message ?? 'บันทึกสำเร็จ');
            this.isSiteModalOpen.set(false);
            this.loadSiteQuotas();
          } else {
            this.toast.error(res?.message ?? res?.Message ?? 'บันทึกไม่สำเร็จ');
          }
          this.finishConfirm(this.isSaving);
        },
        error: () => {
          this.toast.error('เกิดข้อผิดพลาดขณะบันทึก');
          this.finishConfirm(this.isSaving);
        },
      });
    });
  }

  /* ---------- Allocate modal ---------- */

  openAllocateModal(row?: ClientFreeQuota): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    if (!this.allocatableSiteQuotas.length) {
      this.toast.error('ยังไม่มีโควต้าลานที่ใช้งานอยู่ — ตั้งโควต้าลานก่อน');
      return;
    }
    this.allocateLocked = !!row;
    this.allocateClient.set(
      row
        ? {
            id: row.clientId,
            name: row.clientName || `Client #${row.clientId}`,
            taxId: '',
            contact: '',
            telephone: '',
            quotaApproved: row.quotaApproved,
            quotaUsed: row.quotaUsed,
          }
        : null,
    );
    this.allocateVehicleTypeId = row?.vehicleTypeId
      ?? (this.allocatableSiteQuotas.length === 1 ? this.allocatableSiteQuotas[0].vehicleTypeId : 0);
    this.allocateAdditionalQuota = 1;
    this.allocateManagedBy = row?.managedBy || 'site_building';
    this.clientPickerSearch = '';
    this.clientOptions.set([]);
    this.isAllocateModalOpen.set(true);
    if (!row) this.loadClientOptions();
  }

  closeAllocateModal(): void {
    if (this.isAllocating()) return;
    this.isAllocateModalOpen.set(false);
  }

  onClientPickerSearchChange(): void {
    if (this.clientPickerTimer) clearTimeout(this.clientPickerTimer);
    this.clientPickerTimer = setTimeout(() => {
      this.clientPickerTimer = null;
      this.loadClientOptions();
    }, 300);
  }

  pickClient(option: FreeQuotaClientOption): void {
    this.allocateClient.set(option);
  }

  clearPickedClient(): void {
    if (this.allocateLocked) return;
    this.allocateClient.set(null);
  }

  onAllocate(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    const siteId = this.selectedSiteId;
    const client = this.allocateClient();
    if (!siteId) {
      this.toast.error('กรุณาเลือกลาน');
      return;
    }
    if (!client) {
      this.toast.error('กรุณาเลือก Client');
      return;
    }
    if (!this.allocateVehicleTypeId) {
      this.toast.error('กรุณาเลือกประเภทรถ');
      return;
    }
    if (!this.allocateAdditionalQuota || this.allocateAdditionalQuota <= 0) {
      this.toast.error('จำนวนโควต้าเพิ่มต้องมากกว่า 0');
      return;
    }
    const pool = this.allocatePool;
    if (pool && this.allocateAdditionalQuota > pool.quotaRemainingPool) {
      this.toast.error(`โควต้าลานคงเหลือให้แจกได้ ${pool.quotaRemainingPool}`);
      return;
    }

    const auth = this.resolveAuth();
    if (!auth) return;

    const payload: AllocateClientFreeQuotaPayload = {
      ...auth,
      siteId,
      clientId: client.id,
      vehicleTypeId: this.allocateVehicleTypeId,
      additionalQuota: this.allocateAdditionalQuota,
      managedBy: this.allocateManagedBy || 'site_building',
    };

    this.askConfirm({
      title: 'ยืนยันแจกโควต้าให้ Client?',
      message: 'จำนวนที่แจกจะหักจาก pool ของลาน',
      highlight: `${client.name} · ${this.vehicleTypeName(payload.vehicleTypeId)} +${payload.additionalQuota}`,
      variant: 'warning',
      icon: 'share',
    }, () => {
      this.isAllocating.set(true);
      this.confirmLoading = true;
      this.api.allocateClientFreeQuota(payload).subscribe({
        next: (res) => {
          console.log('allocateClientFreeQuota', res);
          if (res?.isSuccess ?? res?.IsSuccess) {
            this.toast.success(res?.message ?? res?.Message ?? 'แจกโควต้าสำเร็จ');
            this.isAllocateModalOpen.set(false);
            this.reloadAfterClientChange();
          } else {
            this.toast.error(res?.message ?? res?.Message ?? 'แจกโควต้าไม่สำเร็จ');
          }
          this.finishConfirm(this.isAllocating);
        },
        error: () => {
          this.toast.error('เกิดข้อผิดพลาดขณะแจกโควต้า');
          this.finishConfirm(this.isAllocating);
        },
      });
    });
  }

  /* ---------- Edit client modal ---------- */

  openEditClientModal(row: ClientFreeQuota): void {
    this.editRow.set(row);
    this.editClientQuotaApproved = row.quotaApproved;
    this.editClientManagedBy = row.managedBy || 'site_building';
    this.isEditModalOpen.set(true);
  }

  closeEditClientModal(): void {
    if (this.isUpdatingClient()) return;
    this.isEditModalOpen.set(false);
    this.editRow.set(null);
  }

  onUpdateClientQuota(): void {
    const row = this.editRow();
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    if (!row) return;
    if (this.editClientQuotaApproved == null || this.editClientQuotaApproved < 0) {
      this.toast.error('จำนวนโควต้าไม่ถูกต้อง');
      return;
    }
    if (this.editClientQuotaApproved < row.quotaUsed) {
      this.toast.error(`ลดได้ไม่ต่ำกว่าที่ใช้แล้ว (${row.quotaUsed})`);
      return;
    }
    const delta = this.editDelta;
    const pool = this.editPool;
    if (delta > 0 && pool && delta > pool.quotaRemainingPool) {
      this.toast.error(`โควต้าลานคงเหลือให้แจกได้ ${pool.quotaRemainingPool}`);
      return;
    }
    const managedChanged = this.editClientManagedBy !== (row.managedBy || 'site_building');
    if (delta === 0 && !managedChanged) {
      this.toast.error('ยังไม่มีการเปลี่ยนแปลง');
      return;
    }

    const actionLabel =
      delta < 0 ? `ลดโควต้า ${-delta} และคืนลาน`
        : delta > 0 ? `เพิ่มโควต้า ${delta} (หักจาก pool ลาน)`
          : 'เปลี่ยนผู้ดูแลโควต้า';

    this.askConfirm({
      title: 'ยืนยันบันทึกโควต้า Client?',
      message: actionLabel,
      highlight: `${row.clientName || row.clientId} · ${row.vehicleTypeName || row.vehicleTypeId}`,
      variant: delta < 0 ? 'danger' : 'warning',
      icon: delta < 0 ? 'trending_down' : 'save',
    }, () => this.executeUpdateClientQuota(row, this.editClientQuotaApproved, this.editClientManagedBy));
  }

  reclaimUnusedClientQuota(row: ClientFreeQuota): void {
    if (!this.canUpdate) return;
    if (row.quotaRemaining <= 0) {
      this.toast.error('ไม่มีส่วนเหลือให้คืน');
      return;
    }
    this.askConfirm({
      title: 'คืนส่วนเหลือกลับลาน?',
      message: 'จะลดโควต้า Client เหลือเท่าที่ใช้แล้ว และคืนส่วนที่เหลือกลับลาน',
      highlight: `${row.clientName || row.clientId} · คืน ${row.quotaRemaining}`,
      variant: 'warning',
      icon: 'undo',
    }, () => this.executeUpdateClientQuota(row, row.quotaUsed, row.managedBy || 'site_building'));
  }

  cancelClientQuota(row: ClientFreeQuota): void {
    if (!this.canUpdate) return;
    if (row.quotaUsed > 0) {
      this.askConfirm({
        title: 'ยกเลิกทั้งแถวไม่ได้',
        message: `มีการใช้แล้ว ${row.quotaUsed} — จะคืนเฉพาะส่วนที่เหลือกลับลานแทน`,
        highlight: `${row.clientName || row.clientId} · คืน ${row.quotaRemaining}`,
        variant: 'warning',
        icon: 'warning',
      }, () => this.executeUpdateClientQuota(row, row.quotaUsed, row.managedBy || 'site_building'));
      return;
    }

    this.askConfirm({
      title: 'ยกเลิกโควต้า Client?',
      message: 'จะลบแถวโควต้านี้และคืนจำนวนทั้งหมดกลับลาน',
      highlight: `${row.clientName || row.clientId} · ${row.vehicleTypeName || row.vehicleTypeId}`,
      variant: 'danger',
      icon: 'delete_forever',
    }, () => this.executeUpdateClientQuota(row, 0, row.managedBy || 'site_building'));
  }

  /* ---------- History ---------- */

  openSiteHistory(row: SiteFreeQuota): void {
    this.openHistory(
      FreeQuotaActivityEntityTypes.SiteFreeQuota,
      row.id,
      'ประวัติโควต้าจอดฟรี (ลาน)',
      `${this.selectedSiteName} · ${row.vehicleTypeName || row.vehicleTypeId}`,
    );
  }

  openClientHistory(row: ClientFreeQuota): void {
    this.openHistory(
      FreeQuotaActivityEntityTypes.ClientFreeQuota,
      row.id,
      'ประวัติโควต้าจอดฟรี (Client)',
      `${row.clientName || row.clientId} · ${row.vehicleTypeName || row.vehicleTypeId}`,
    );
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  /* ---------- Confirm ---------- */

  askConfirm(
    opts: {
      title: string;
      message: string;
      highlight?: string;
      variant?: PssConfirmModalVariant;
      icon?: string;
    },
    action: () => void,
  ): void {
    this.confirmTitle = opts.title;
    this.confirmMessage = opts.message;
    this.confirmHighlight = opts.highlight ?? '';
    this.confirmMessageSuffix = 'กด ใช่ เพื่อยืนยัน หรือ ไม่ เพื่อยกเลิก';
    this.confirmVariant = opts.variant ?? 'warning';
    this.confirmIcon = opts.icon ?? 'help';
    this.confirmLoading = false;
    this.pendingConfirmAction = action;
    this.showConfirm = true;
  }

  onConfirmYes(): void {
    const action = this.pendingConfirmAction;
    this.pendingConfirmAction = null;
    action?.();
  }

  onConfirmNo(): void {
    if (this.confirmLoading) return;
    this.showConfirm = false;
    this.pendingConfirmAction = null;
  }

  /* ---------- Private ---------- */

  private vehicleTypeName(id: number): string {
    return this.vehicleTypes().find((v) => v.id === id)?.name
      || this.siteQuotas().find((q) => q.vehicleTypeId === id)?.vehicleTypeName
      || `ประเภท #${id}`;
  }

  private openHistory(entityType: string, id: number, title: string, subtitle: string): void {
    const entityId = freeQuotaActivityEntityId(id);
    if (entityId <= 0) {
      this.toast.error('ยังไม่มีประวัติของรายการนี้');
      return;
    }
    this.historyEntityType = entityType;
    this.historyEntityId = entityId;
    this.historyTitle = title;
    this.historySubtitle = subtitle;
    this.showActivityHistory = true;
  }

  private executeUpdateClientQuota(row: ClientFreeQuota, quotaApproved: number, managedBy: string): void {
    const auth = this.resolveAuth();
    if (!auth) {
      this.showConfirm = false;
      return;
    }

    const payload: UpdateClientFreeQuotaPayload = {
      ...auth,
      id: row.id,
      quotaApproved,
      managedBy,
    };

    this.isUpdatingClient.set(true);
    this.confirmLoading = true;
    this.api.updateClientFreeQuota(payload).subscribe({
      next: (res) => {
        console.log('updateClientFreeQuota', res);
        if (res?.isSuccess ?? res?.IsSuccess) {
          this.toast.success(res?.message ?? res?.Message ?? 'อัปเดตโควต้า Client สำเร็จ');
          this.isEditModalOpen.set(false);
          this.editRow.set(null);
          this.reloadAfterClientChange();
        } else {
          this.toast.error(res?.message ?? res?.Message ?? 'อัปเดตไม่สำเร็จ');
        }
        this.finishConfirm(this.isUpdatingClient);
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาดขณะอัปเดตโควต้า Client');
        this.finishConfirm(this.isUpdatingClient);
      },
    });
  }

  private finishConfirm(busy: { set(value: boolean): void }): void {
    busy.set(false);
    this.confirmLoading = false;
    this.showConfirm = false;
  }

  private reloadAfterClientChange(): void {
    this.loadSiteQuotas();
    this.loadClientTable();
  }

  private closeAllModals(): void {
    this.isSiteModalOpen.set(false);
    this.isAllocateModalOpen.set(false);
    this.isEditModalOpen.set(false);
    this.editRow.set(null);
    this.showActivityHistory = false;
  }

  private resetClientFilters(): void {
    this.clientSearch = '';
    this.filterVehicleTypeId = 0;
    this.filterManagedBy = '';
    this.clientPage.set(1);
  }

  private resolveAuth(): { userId: number; menuId: number } | null {
    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return null;
    }
    return { userId, menuId };
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  // ================= API Functions =================

  private loadAll(): void {
    this.loadSiteQuotas();
    this.loadFreeSubscriptions();
    this.loadClientTable();
  }

  private loadSiteQuotas(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) return;
    this.isSiteLoading.set(true);
    this.api.getSiteFreeQuotas(siteId).subscribe({
      next: (res) => {
        console.log('getSiteFreeQuotas', res);
        if (siteId !== this.selectedSiteId) return;
        this.siteQuotas.set(mapSiteFreeQuotaList(res));
        this.isSiteLoading.set(false);
      },
      error: () => {
        this.siteQuotas.set([]);
        this.isSiteLoading.set(false);
        this.toast.error('โหลดโควต้าลานล้มเหลว');
      },
    });
  }

  private loadClientTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) return;
    const seq = ++this.clientRequestSeq;
    this.isClientLoading.set(true);
    this.api
      .getClientFreeQuotasForTable({
        siteId,
        search: this.clientSearch,
        vehicleTypeId: this.filterVehicleTypeId,
        managedBy: this.filterManagedBy,
        page: this.clientPage(),
        pageSize: this.clientPageSize(),
      })
      .subscribe({
        next: (res) => {
          console.log('getClientFreeQuotasForTable', res);
          if (seq !== this.clientRequestSeq) return;
          this.clientRows.set(mapClientFreeQuotaList(res?.data));
          this.clientSummary.set(mapClientFreeQuotaSummary(res?.summary));
          this.clientTotal.set(Number(res?.total ?? 0));
          this.clientTotalPages.set(Number(res?.totalPages ?? 0));
          this.isClientLoading.set(false);
        },
        error: () => {
          if (seq !== this.clientRequestSeq) return;
          this.clientRows.set([]);
          this.isClientLoading.set(false);
          this.toast.error('โหลดโควต้า Client ล้มเหลว');
        },
      });
  }

  private loadClientOptions(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) return;
    const seq = ++this.optionsRequestSeq;
    this.isClientOptionsLoading.set(true);
    this.api.getFreeQuotaClientOptions(siteId, this.clientPickerSearch).subscribe({
      next: (res) => {
        console.log('getFreeQuotaClientOptions', res);
        if (seq !== this.optionsRequestSeq) return;
        this.clientOptions.set(mapFreeQuotaClientOptionList(res));
        this.isClientOptionsLoading.set(false);
      },
      error: () => {
        if (seq !== this.optionsRequestSeq) return;
        this.clientOptions.set([]);
        this.isClientOptionsLoading.set(false);
        this.toast.error('โหลดรายชื่อ Client ล้มเหลว');
      },
    });
  }

  private loadFreeSubscriptions(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.freeSubscriptions.set([]);
      return;
    }
    this.api.getFreeParkingSubscriptions(siteId).subscribe({
      next: (res) => this.freeSubscriptions.set(mapFreeParkingSubscriptionList(res)),
      error: () => this.freeSubscriptions.set([]),
    });
  }

  private loadVehicleTypes(): void {
    this.apiMaster.getParkingVehicleTypeForTable({ page: 1, pageSize: 200 }).subscribe({
      next: (res) => {
        const mapped = this.masterMapper.mapTable<ParkingVehicleTypeDto, ParkingVehicleType>(
          res,
          (item) => this.masterMapper.mapVehicleType(item),
        );
        this.vehicleTypes.set(mapped.data);
      },
      error: () => this.vehicleTypes.set([]),
    });
  }
}
